import { useCallback, type ReactNode } from 'react'
import { ButtonEdit, ComplianceViolationsList, EmptyComplianceViolations, NotReady, Page } from '@components'
import { useCompliancePolicy, useComplianceViolations, useUser } from '@front/model'
import { useNavigate, createFileRoute } from '@tanstack/react-router'
import {
  type WithComplianceViolationId,
  type CompliancePolicyId,
  type UserId,
  getName,
  isUserId,
  isNotEmpty,
  isCompliancePolicyId,
} from '@terros/common'
import { useRequiredParams } from '@util'

type Params = {
  entityId: CompliancePolicyId | UserId
}

function ViewCompliancePolicyViolations(): ReactNode {
  const { entityId } = useRequiredParams<Params>()
  const navigate = useNavigate()

  const { data, isLoading, error, refetch, isFetching } = useComplianceViolations(entityId)

  const onClick = useCallback(
    ({ violationId }: WithComplianceViolationId): void => {
      navigate({ to: `/compliance/violation/${violationId}` })
    },
    [navigate]
  )

  const onEditClick = useCallback(
    () => navigate({ to: `../../policy/${entityId}` }),
    [navigate, entityId]
  )

  const title = isUserId(entityId) ? UserTitle({ userId: entityId }) : PolicyTitle({ policyId: entityId })

  return (
    <Page>
      <Page.Heading
        title={'Violations: ' + title}
        back
        goBackBehavior='history'
        refetch={refetch}
        isFetching={isFetching}
      >
        {isCompliancePolicyId(entityId) && (
          <ButtonEdit variant='ghost' onClick={onEditClick}>
            Edit Policy
          </ButtonEdit>
        )}
      </Page.Heading>
      <Page.Content scrollArea>
        <NotReady data={data} error={error} id='Violations' isLoading={isLoading}>
          {isNotEmpty(data) ? (
            <ComplianceViolationsList violations={data} onClick={onClick} />
          ) : (
            <EmptyComplianceViolations />
          )}
        </NotReady>
      </Page.Content>
    </Page>
  )
}

const UserTitle = ({ userId }: { userId: UserId }): string => {
  const { data: user } = useUser(userId)
  return user ? getName(user) : 'Unknown User'
}

const PolicyTitle = ({ policyId }: { policyId: CompliancePolicyId }): string => {
  const { data: policy } = useCompliancePolicy(policyId)
  return policy?.title ?? 'Unknown Policy'
}

export const Route = createFileRoute('/compliance_/violations_/$entityId/')({
  component: ViewCompliancePolicyViolations,
})
